import type { JSX } from 'react';
import { SkeletonAvatar, SkeletonBlock } from '@taki/ui';

/**
 * Skeletons sur mesure des ecrans de Cat Manager Six.
 *
 * Le gabarit fournit deja un skeleton par defaut (SkeletonListRows) pour la
 * liste "Les chats" : elle EST le pattern de reference (avatar 30 + nom + sous
 * ligne + filet), rien a surcharger. Ici on ne compose que les deux vues qui
 * DIFFERENT de ce pattern :
 *
 *  - Reservations : jauge "Capacite du jour" (deux mesures + pastille) suivie de
 *    lignes de creneau (horaire, place, nombre, statut, deux actions), sans
 *    avatar ;
 *  - Fiche du chat : profil (grosse photo + statut + champs) puis historique de
 *    soins, une mise en page qui n'est pas une liste du tout.
 *
 * Regles : 100% primitives @taki/ui (SkeletonBlock, SkeletonAvatar), rayons via
 * les tokens (--radius-*), largeurs en rem, memes classes de structure que le
 * rendu reel pour ne pas sauter au swap skeleton -> contenu. Tout est decoratif
 * (les primitives sont aria-hidden ; le gabarit porte deja aria-busy).
 */

/* Rayons repris des tokens (jamais de px/hex en dur). BADGE = rayon exact de
   StatusBadge (rounded-[0.5625rem]). */
const XS = 'var(--radius-xs)';
const SM = 'var(--radius-sm)';
const BADGE = '0.5625rem';

/* Largeurs deterministes (pas d'aleatoire : rendu stable), cyclees par index. */
const SLOT_WIDTHS = ['4.5rem', '5rem', '4.25rem', '4.75rem', '4.5rem', '5.25rem'];
const CLIENT_WIDTHS = ['6.5rem', '5rem', '7.5rem', '6rem', '8rem', '5.5rem'];

/**
 * Barre fine de texte (memes proportions que les barres du kit : rayon xs).
 * `w` optionnel comme SkeletonBlock (largeurs cyclees = string | undefined sous
 * noUncheckedIndexedAccess) : une largeur absente retombe sur le defaut du
 * primitif, jamais atteint ici (index toujours dans les bornes).
 */
function TextBar({ w, h = '0.875rem' }: { w?: string; h?: string }): JSX.Element {
  return <SkeletonBlock w={w} h={h} rounded={XS} />;
}

/* ------------------------------------------------------------------ */
/* Reservations                                                        */
/* ------------------------------------------------------------------ */

export interface ReservationsSkeletonProps {
  /** Reproduire la jauge "Capacite du jour" (vue Aujourd'hui uniquement). */
  withGauge?: boolean;
  /** Nombre de lignes de creneau factices. Defaut 6. */
  rows?: number;
}

/** Une ligne de la jauge : mesure (icone + libelle) a gauche, pastille a droite. */
function GaugeRow({ textW, badgeW }: { textW: string; badgeW: string }): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <SkeletonBlock w="1rem" h="1rem" rounded={XS} />
        <TextBar w={textW} />
      </div>
      <SkeletonBlock w={badgeW} h="2rem" rounded={BADGE} />
    </div>
  );
}

/** Une ligne de creneau (mirror exact de la ligne de visite, zone 5). */
function VisitRow({ index }: { index: number }): JSX.Element {
  const hasClient = index % 3 !== 1;
  return (
    <div className="flex items-center gap-3.5 border-b border-border-soft px-5 py-2.5">
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Ligne horaire : icone stopwatch + heure (text-sm, boite 1.25rem). */}
        <div className="flex h-5 items-center gap-2">
          <SkeletonBlock w="1rem" h="1rem" rounded={XS} />
          <TextBar w={SLOT_WIDTHS[index % SLOT_WIDTHS.length]} />
        </div>
        {/* Nom du client (text-xs, boite 1rem, aligne sous l'heure) : optionnel. */}
        {hasClient && (
          <div className="flex h-4 items-center pl-6">
            <TextBar w={CLIENT_WIDTHS[index % CLIENT_WIDTHS.length]} h="0.625rem" />
          </div>
        )}
      </div>
      {/* Type de place (icone 14 + libelle). */}
      <div className="flex shrink-0 items-center gap-2">
        <SkeletonBlock w="0.875rem" h="0.875rem" rounded={XS} />
        <TextBar w="2.25rem" h="0.75rem" />
      </div>
      {/* Nombre de personnes (icone 14 + libelle). */}
      <div className="flex shrink-0 items-center gap-2">
        <SkeletonBlock w="0.875rem" h="0.875rem" rounded={XS} />
        <TextBar w="4rem" h="0.75rem" />
      </div>
      {/* Pastille de statut. */}
      <SkeletonBlock w="5.5rem" h="2rem" rounded={BADGE} />
      {/* Actions Arrivee / Annuler (boutons sm, h-8, rayon sm). */}
      <div className="flex shrink-0 gap-1.5">
        <SkeletonBlock w="4.5rem" h="2rem" rounded={SM} />
        <SkeletonBlock w="4.75rem" h="2rem" rounded={SM} />
      </div>
    </div>
  );
}

export function ReservationsSkeleton({
  withGauge = true,
  rows = 6,
}: ReservationsSkeletonProps): JSX.Element {
  return (
    <div>
      {withGauge && (
        <section className="border-b border-border-soft px-5 pb-4 pt-4">
          {/* Titre "Capacite du jour" (text-sm, boite 1.25rem, pb-3). */}
          <div className="pb-3">
            <div className="flex h-5 items-center">
              <TextBar w="6.5rem" />
            </div>
          </div>
          <div className="flex flex-col gap-3">
            <GaugeRow textW="6rem" badgeW="6.25rem" />
            <GaugeRow textW="5.5rem" badgeW="5.75rem" />
          </div>
        </section>
      )}
      <div>
        {Array.from({ length: Math.max(1, rows) }, (_, index) => (
          <VisitRow key={index} index={index} />
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Fiche du chat                                                       */
/* ------------------------------------------------------------------ */

/* Largeurs des faux boutons (statut, caractere) : cyclees, rendu stable. */
const STATUS_BTN_WIDTHS = ['4.5rem', '5.5rem', '4rem', '5rem'];
const TRAIT_BTN_WIDTHS = ['4rem', '5rem', '4.5rem'];

/** Titre de section (h3 text-sm font-semibold, pb-3). */
function SectionTitle({ w }: { w: string }): JSX.Element {
  return (
    <div className="pb-3">
      <div className="flex h-5 items-center">
        <TextBar w={w} />
      </div>
    </div>
  );
}

/** Une ligne d'historique de soin (mirror de la ligne de soin). */
function CareRow(): JSX.Element {
  return (
    <li className="flex items-start gap-3 border-b border-border-soft py-2.5 last:border-b-0">
      <SkeletonBlock w="1rem" h="1rem" rounded={XS} className="mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex h-5 items-center">
          <TextBar w="5rem" />
        </div>
        <div className="flex h-4 items-center">
          <TextBar w="8rem" h="0.625rem" />
        </div>
      </div>
      <SkeletonBlock w="3.5rem" h="0.75rem" rounded={XS} className="shrink-0" />
    </li>
  );
}

export function CatDetailSkeleton(): JSX.Element {
  return (
    <div className="pb-8">
      {/* Bouton "Retour" (ghost sm). */}
      <div className="px-5 pt-4">
        <SkeletonBlock w="4rem" h="2rem" rounded={SM} />
      </div>

      {/* Profil : photo + statut + champs, a plat (regle G9). */}
      <section className="border-b border-border-soft px-5 pb-5 pt-4">
        <SectionTitle w="3rem" />
        <div className="flex flex-col gap-4">
          {/* Photo + statut courant. */}
          <div className="flex items-center gap-4">
            <SkeletonAvatar size={64} />
            <div className="flex flex-col gap-1.5">
              <TextBar w="2.5rem" h="0.75rem" />
              <SkeletonBlock w="6rem" h="2rem" rounded={BADGE} />
            </div>
          </div>
          {/* Changer le statut (libelle + 4 boutons). */}
          <div className="flex flex-col gap-1.5">
            <TextBar w="6.5rem" h="0.75rem" />
            <div className="flex flex-wrap gap-2">
              {STATUS_BTN_WIDTHS.map((w, index) => (
                <SkeletonBlock key={index} w={w} h="2rem" rounded={SM} />
              ))}
            </div>
          </div>
          {/* Caractere (libelle + boutons). */}
          <div className="flex flex-col gap-1.5">
            <TextBar w="4.5rem" h="0.75rem" />
            <div className="flex flex-wrap gap-2">
              {TRAIT_BTN_WIDTHS.map((w, index) => (
                <SkeletonBlock key={index} w={w} h="2rem" rounded={SM} />
              ))}
            </div>
          </div>
          {/* Sante et quotidien (3 champs). */}
          <div className="grid gap-4 sm:grid-cols-3">
            {[0, 1, 2].map((index) => (
              <div key={index} className="flex flex-col gap-1.5">
                <TextBar w="4rem" h="0.75rem" />
                <TextBar w="5rem" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Soins : historique a plat. */}
      <section className="border-b border-border-soft px-5 pb-5 pt-4">
        <SectionTitle w="2.5rem" />
        <ul className="flex flex-col">
          {[0, 1, 2].map((index) => (
            <CareRow key={index} />
          ))}
        </ul>
      </section>
    </div>
  );
}
